/**
 * Add Players — /(app)/games/[id]/players
 *
 * Host-only LOBBY screen focused on ONE job: filling open seats.
 *   • Search to invite a new user (existing InvitePlayerSearch).
 *   • Add a bot.
 *   • Share the invite code / link.
 *
 * The joined roster and the kick affordance deliberately DON'T live here —
 * the host manages seats (including kicking) inline on the lobby seat map.
 * This screen is purely additive.
 *
 * The lobby now offers invite, bots and sharing inline (seat actions and the
 * Invite card), so nothing in the app links here any more; the route stays
 * for deep links and bookmarks. It shares the bot hook/picker and the invite
 * link format with the lobby.
 */

import { COMPACT_HEADER_STYLE } from '../../../../lib/gameChrome'
import React from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Share,
  Platform,
  useWindowDimensions,
} from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { PlayerColorChip } from '@warcarp/ui'
import { teamsConfigOf } from '@warcarp/api-types'
import { useGame } from '../../../../hooks/useGames'
import { computeSeatMap } from '../../../../lib/seatMap'
import { playerColorSlot } from '../../../../lib/playerColor'
import { useGameSocket } from '../../../../hooks/useGameSocket'
import { useAuth } from '../../../../hooks/useAuth'
import { Button } from '../../../../components/ui/Button'
import { InvitePlayerSearch } from '../../../../components/game/InvitePlayerSearch'
import { useOrientation } from '../../../../hooks/useOrientation'
import { useLobbyBots } from '../../../../hooks/useLobbyBots'
import { inviteLinkUrl } from '../../../../lib/lobbySeats'
import { PlayerVacationIndicator } from '../../../../components/settings/PlayerVacationIndicator'
import { BotPickerModal } from '../../../../components/game/lobby/BotPickerModal'

export default function PlayerAdminScreen() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const router = useRouter()
  const { user } = useAuth()
  const { data: game, isLoading } = useGame(id)
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(560, winWidth * 0.6) : undefined
  const bots = useLobbyBots(game)
  const [botPickerOpen, setBotPickerOpen] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  React.useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(t)
  }, [copied])

  // Subscribe to the game socket so a host start / status change on
  // another client lands in this screen's cache without the 15 s poll
  // delay. Required for the auto-route effect below to fire promptly.
  useGameSocket(id, !!id && !!game)

  // When the game leaves LOBBY (host started it, server cancelled it,
  // etc.), bounce the user back into the main game screen so they end
  // up looking at the live game instead of the static "Game has
  // started" stub below. router.replace so Back doesn't return here.
  React.useEffect(() => {
    if (!id || !game) return
    if (game.status !== 'LOBBY') {
      router.replace(`/(app)/games/${id}`)
    }
  }, [id, game?.status, router])

  if (isLoading || !game) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center">
        <ActivityIndicator color="#ef4444" />
      </SafeAreaView>
    )
  }

  const isHost = game.adminId === user?.id
  if (!isHost) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center gap-4 px-6">
        <Text className="text-ink font-stencil text-lg uppercase">Host only</Text>
        <Button title="Back to game" variant="secondary" onPress={() => router.replace(`/(app)/games/${id}`)} />
      </SafeAreaView>
    )
  }

  if (game.status !== 'LOBBY') {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center gap-4 px-6">
        <Text className="text-ink font-stencil text-lg uppercase">Game has started</Text>
        <Text className="text-ink-soft font-typed text-sm text-center">
          The roster is locked once play begins.
        </Text>
        <Button title="Back to game" variant="secondary" onPress={() => router.replace(`/(app)/games/${id}`)} />
      </SafeAreaView>
    )
  }

  const pendingInvitations = game.invitations.filter((i) => i.status === 'pending')
  // ACTUAL players only — pending invitees get their own set below so the
  // search shows a disabled "Invited" button, not the misleading "// in lobby".
  const existingUserIds = new Set(game.players.map((p) => p.userId))
  const invitedUserIds = new Set(pendingInvitations.map((i) => i.user.id))
  // Users who DECLINED an invite to this game — final; the search renders a
  // disabled "Declined" button for them (the API 409s on re-invite anyway).
  const declinedUserIds = new Set(
    game.invitations.filter((i) => i.status === 'declined').map((i) => i.user.id),
  )
  // Seat map drives the "can invite / add bot" gating: a seat is open only
  // when it's neither taken nor reserved for a pending invite.
  const seats = computeSeatMap(game.players, game.invitations, game.maxPlayers)
  const seatsOpen = seats.filter((s) => s.kind === 'open').length

  // Pick-seats team lobbies: these buttons take the lowest open seats; the
  // lobby's per-seat Add bot chooses the team.
  const pickSeats = teamsConfigOf(game?.setupConfig)?.assignment === 'manual'

  // The join URL anyone can open (or paste) to join — routes through the
  // invite landing, which handles sign-up/login for logged-out recipients.
  const inviteUrl = game.inviteCode ? inviteLinkUrl(game.inviteCode) : ''

  async function handleCopyInvite() {
    if (!inviteUrl) return
    // Web: copy straight to the clipboard (no native dep needed). Native: the
    // OS share sheet is the copy/share affordance (a Copy action lives in it).
    if (Platform.OS === 'web') {
      try {
        await navigator.clipboard.writeText(inviteUrl)
        setCopied(true)
        return
      } catch {
        // Clipboard blocked (permissions / insecure context) — fall through.
      }
    }
    const subject = game?.name?.trim()
      ? `Join my WarCarp game "${game.name.trim()}"`
      : 'Join my WarCarp game'
    try {
      await Share.share({ message: `${subject}: ${inviteUrl}`, url: inviteUrl })
    } catch {
      // user cancelled or share failed — no-op
    }
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <View className="flex-row items-center px-4 border-b border-olive gap-3" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          // Back falls back to an explicit lobby replace when the
          // history stack has nothing to pop (deep-linked entry, or
          // a router.replace earlier in the session that wiped the
          // push of /players). Without the fallback the tap silently
          // no-ops, which is what the user was seeing.
          onPress={() => {
            if (router.canGoBack()) {
              router.back()
            } else {
              router.replace(`/(app)/games/${id}`)
            }
          }}
          accessibilityLabel="Back to lobby"
          accessibilityRole="button"
          className="p-1"
          // Generous hit-slop so the small ← Back text registers taps
          // even with imprecise touches near the screen edge.
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Text className="text-blood font-typed text-base">← Back</Text>
        </TouchableOpacity>
        <Text className="flex-1 text-ink font-stencil text-lg uppercase">
          Add Players
        </Text>
      </View>

      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          paddingVertical: 16,
          gap: 20,
          alignItems: isLandscape ? 'center' : undefined,
        }}
        keyboardShouldPersistTaps="handled"
      >
        <View style={isLandscape ? { width: contentWidth } : { width: '100%' }}>

        {/* ── Find & invite ──────────────────────────────────────────── */}
        {seatsOpen > 0 ? (
          <View className="mx-4">
            <InvitePlayerSearch
              gameId={id ?? ''}
              existingUserIds={existingUserIds}
              declinedUserIds={declinedUserIds}
              invitedUserIds={invitedUserIds}
              embedded
            />
          </View>
        ) : (
          <Text className="mx-4 text-ink-soft font-typed text-xs italic text-center">
            // all seats full — kick a player from the lobby to open one
          </Text>
        )}

        {/* Bot actions share one compact row: pick specific profiles or fill
            every currently open (unreserved) seat with random profiles. */}
        {seatsOpen > 0 && (
          <View className="mx-4 mt-4 gap-2">
            <View className="gap-0.5">
              <Text className="text-ink-soft text-[10px] font-stencil uppercase tracking-widest">
                Bot players
              </Text>
              <Text className="text-ink-soft font-typed text-xs">
                {pickSeats
                  ? 'Choose specific bots, or fill every open seat with random bots. Bots take the lowest open seats; to pick a team, use Add bot on that seat in the lobby.'
                  : 'Choose specific bots, or fill every open seat with random bots.'}
              </Text>
            </View>
            <View className="flex-row gap-2">
              <View className="flex-1">
                <Button
                  title="Choose Bots"
                  variant="secondary"
                  size="sm"
                  onPress={() => setBotPickerOpen(true)}
                  disabled={bots.isPending || bots.isFilling}
                  accessibilityLabel="Choose bot players"
                />
              </View>
              <View className="flex-1">
                <Button
                  title={bots.isFilling ? 'Filling Seats…' : 'Fill with Random Bots'}
                  variant="secondary"
                  size="sm"
                  onPress={() => void bots.fillWithBots()}
                  disabled={bots.isPending || bots.isFilling}
                  accessibilityLabel="Fill remaining seats with random bots"
                />
              </View>
            </View>
          </View>
        )}

        {/* Inline add-bot feedback (no toasts on this admin screen). */}
        {bots.actionError && (
          <Text className="mx-4 mt-3 text-blood font-typed text-xs">{bots.actionError}</Text>
        )}

        {/* ── Players & invites (live) ─────────────────────────────────
            Read-only confirmation of who's in the game and who's been
            invited, so adding a bot / inviting someone shows up here
            immediately. Kicking still lives on the lobby seat map. */}
        <View className="mx-4 mt-4 gap-2">
          <View className="flex-row items-center justify-between">
            <Text className="text-ink-soft text-[10px] font-stencil uppercase tracking-widest">
              Players & invites ({game.players.length}/{game.maxPlayers})
            </Text>
            {bots.addedNote && (
              <Text className="text-olive-up font-typed text-xs">✓ {bots.addedNote}</Text>
            )}
          </View>
          <View className="gap-1.5">
            {seats
              .filter((s) => s.kind !== 'open')
              .map((seat) =>
                seat.kind === 'filled' ? (
                  <View
                    key={seat.slot}
                    className="flex-row items-center gap-2 rounded-md bg-paper-up px-2.5 py-1.5"
                    accessibilityLabel={`${seat.player.displayName}${seat.player.userId === game.adminId ? ', host' : ''}${seat.player.isBot ? ', bot' : ''}`}
                  >
                    <PlayerColorChip slot={playerColorSlot(seat.player)} size="sm" />
                    <View className="flex-1 flex-row items-center gap-2">
                    <Text className="text-ink font-typed text-xs" style={{ flexShrink: 1 }} numberOfLines={1}>
                      {seat.player.displayName}
                      {seat.player.isBot ? ' (bot)' : ''}
                      {seat.player.userId === game.adminId ? ' (host)' : ''}
                    </Text>
                    <PlayerVacationIndicator name={seat.player.displayName} vacation={seat.player.vacation} />
                    </View>
                  </View>
                ) : (
                  <View
                    key={seat.slot}
                    className="flex-row items-center gap-2 rounded-md bg-paper-up border border-olive px-2.5 py-1.5 opacity-70"
                    accessibilityLabel={`Invited: ${seat.inviteeName}`}
                  >
                    {/* No colour chip: an invited player hasn't chosen a colour
                        yet, and the seat slot is NOT a colour. Neutral dashed
                        placeholder until they join and pick. */}
                    <View
                      style={{ width: 20, height: 20, borderRadius: 10, borderWidth: 1.5, borderColor: '#6b7d3a', borderStyle: 'dashed' }}
                    />
                    <Text className="flex-1 text-ink-soft font-typed text-xs italic" numberOfLines={1}>
                      {seat.inviteeName} · invited
                    </Text>
                  </View>
                ),
              )}
          </View>
        </View>

        {/* ── Invite link ────────────────────────────────────────────── */}
        {seatsOpen > 0 && game.inviteCode && (
          <>
            <View className="mx-4 my-4 h-px bg-olive" />
            <View className="mx-4">
              <View className="bg-paper-up border border-olive rounded-md p-3 gap-2">
                <Text className="text-ink-soft text-[10px] font-stencil uppercase tracking-widest">
                  Invite Link
                </Text>
                {/* The full URL — recipients open (or paste) it to join; logged-out
                    users are walked through sign-up first. Selectable as a manual
                    fallback if the copy button is unavailable. */}
                <Text selectable testID="invite-url" className="text-ink font-typed text-sm">
                  {inviteUrl}
                </Text>
                <View className="flex-row items-center justify-between gap-2 mt-1">
                  <Text className="text-ink-soft font-typed text-xs">
                    Code <Text className="text-ink tracking-widest">{game.inviteCode}</Text>
                  </Text>
                  <TouchableOpacity
                    onPress={handleCopyInvite}
                    accessibilityLabel={copied ? 'Invite link copied' : 'Copy invite link'}
                    accessibilityRole="button"
                    className={`rounded-md px-3 py-2 items-center justify-center ${copied ? 'bg-paper border border-olive' : 'bg-olive'}`}
                    style={{ minHeight: 36 }}
                  >
                    <Text
                      className={`font-stencil uppercase tracking-wider text-xs ${copied ? 'text-ink' : 'text-paper'}`}
                    >
                      {copied ? '✓ Copied' : Platform.OS === 'web' ? 'Copy invite' : 'Share invite'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </>
        )}
        </View>
      </ScrollView>

      <BotPickerModal
        visible={botPickerOpen}
        onClose={() => setBotPickerOpen(false)}
        players={game.players}
        statuses={bots.statuses}
        onAdd={(profile) => void bots.addBot(profile)}
        busy={bots.isPending || bots.isFilling}
        seatsOpen={seatsOpen}
      />
      {/* Sticky bottom Done — sits flush with the screen edge so the
          host always has a one-tap path back to the lobby regardless
          of how far down they've scrolled. In landscape, cap the button
          width so it matches the content column above. */}
      <View className="border-t border-olive bg-canvas">
        <View
          style={
            isLandscape
              ? {
                  alignSelf: 'center',
                  width: contentWidth,
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                }
              : { paddingHorizontal: 16, paddingVertical: 12 }
          }
        >
          <Button
            title="Done"
            onPress={() => router.replace(`/(app)/games/${id}`)}
            accessibilityLabel="Done — back to lobby"
          />
        </View>
      </View>
    </SafeAreaView>
  )
}
